/* ══════════════════════════════════════════════════════
   Monmouth Cards — shared dialog / drawer accessibility helper
   assets/monmouth-dialog.js   (audit batch 2, ENG-08)

   One place for what every slide-in panel needs:
     • closed panels are not focusable (the `inert` attribute),
     • while a panel is open everything outside it is inert,
     • Tab / Shift+Tab stay inside the open panel,
     • focus moves into the panel on open and returns to the opener on close,
     • Escape closes (only when the caller asks for it — the cart drawer and
       the mobile drawer already own their Escape handling).

   API (window.mcDialog):
     mcDialog.open(panel, { opener, keep: [els], initialFocus: el, escape: fn })
     mcDialog.close(panel, { restore: false })
     mcDialog.closed(panel)      → mark a panel as closed (inert) without it having been opened

   Background elements are reference-counted, so overlapping or nested panels
   never leave something stuck inert; a panel that is swapped out of the DOM
   (filter sheet re-render) is cleaned up automatically on the next open.
   ══════════════════════════════════════════════════════ */
(function () {
  'use strict';
  if (window.mcDialog) return;

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),summary,iframe,[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';
  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, LINK: 1, TEMPLATE: 1, NOSCRIPT: 1, META: 1 };

  var held = new Map();   // element -> number of open panels keeping it inert
  var stack = [];         // open panels, last = top

  function isShown(el) {
    if (!el.getClientRects().length) return false;
    var cs = window.getComputedStyle(el);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  }

  function focusables(panel) {
    return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
      return !el.closest('[inert]') && !el.closest('[hidden]') && isShown(el) && el.tabIndex !== -1;
    });
  }

  function setInert(el, on) {
    try {
      if (on) { el.setAttribute('inert', ''); } else { el.removeAttribute('inert'); }
      el.inert = !!on;
    } catch (e) { /* no-op */ }
  }

  function holdBackground(panel, keep) {
    var mine = [];
    var node = panel;
    while (node && node !== document.body && node.parentElement) {
      var parent = node.parentElement;
      Array.prototype.forEach.call(parent.children, function (sib) {
        if (sib === node || SKIP_TAGS[sib.tagName]) return;
        if (sib.id === 'mc-live-region') return;                 /* announcements must still be heard */
        if (keep && keep.indexOf(sib) !== -1) return;            /* e.g. the click-to-close overlay */
        if (keep && keep.some(function (k) { return k && sib.contains(k); })) return;
        var n = held.get(sib) || 0;
        if (n === 0 && sib.hasAttribute('inert')) return;        /* inert for some other reason: not ours to touch */
        held.set(sib, n + 1);
        if (n === 0) setInert(sib, true);
        mine.push(sib);
      });
      node = parent;
    }
    return mine;
  }

  function restoreReleased(list) {
    (list || []).forEach(function (el) { if ((held.get(el) || 0) > 0) setInert(el, true); });
  }

  function releaseBackground(list) {
    list.forEach(function (el) {
      var n = (held.get(el) || 0) - 1;
      if (n <= 0) { held.delete(el); setInert(el, false); } else { held.set(el, n); }
    });
  }

  function indexOfPanel(panel) {
    for (var i = 0; i < stack.length; i++) if (stack[i].panel === panel) return i;
    return -1;
  }

  /* panels that were swapped out of the DOM while "open" (AJAX re-render) */
  function purgeDetached() {
    var carried = null;
    for (var i = stack.length - 1; i >= 0; i--) {
      if (!document.documentElement.contains(stack[i].panel)) {
        carried = carried || stack[i].opener;
        releaseBackground(stack[i].held);
        restoreReleased(stack[i].released);
        stack.splice(i, 1);
      }
    }
    return carried;
  }

  function open(panel, opts) {
    if (!panel) return;
    opts = opts || {};
    var carried = purgeDetached();
    var existing = indexOfPanel(panel);
    if (existing !== -1) {                                        /* already open: keep state, refresh nothing */
      setInert(panel, false);
      return;
    }
    var opener = opts.opener || carried || document.activeElement;
    if (opener === document.body || opener === document.documentElement) opener = null;

    setInert(panel, false);
    /* A panel opened on top of another one (e.g. the cart drawer over the filter sheet) can sit inside a container the
       first panel made inert: lift that for as long as this panel is open. */
    var released = [];
    for (var anc = panel.parentElement; anc && anc !== document.documentElement; anc = anc.parentElement) {
      if ((held.get(anc) || 0) > 0) { setInert(anc, false); released.push(anc); }
    }
    var entry = {
      panel: panel,
      opener: opener,
      escape: typeof opts.escape === 'function' ? opts.escape : null,
      held: holdBackground(panel, opts.keep),
      released: released
    };
    stack.push(entry);

    var target = opts.initialFocus && !opts.initialFocus.closest('[inert]') ? opts.initialFocus : (focusables(panel)[0] || null);
    if (!target) {
      if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      target = panel;
    }
    try { target.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
  }

  function close(panel, opts) {
    if (!panel) return;
    opts = opts || {};
    var i = indexOfPanel(panel);
    var entry = i !== -1 ? stack.splice(i, 1)[0] : null;
    if (entry) { releaseBackground(entry.held); restoreReleased(entry.released); }
    setInert(panel, true);                                        /* closed panels are not focusable */
    if (entry && opts.restore !== false) {
      var o = entry.opener;
      if (o && !document.documentElement.contains(o) && o.id) o = document.getElementById(o.id);   /* re-rendered trigger */
      if (o && o.focus && document.documentElement.contains(o)) {
        try { o.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
      }
    }
  }

  function closed(panel) { if (panel) setInert(panel, true); }

  document.addEventListener('keydown', function (e) {
    if (!stack.length) return;
    var top = stack[stack.length - 1];
    if (!document.documentElement.contains(top.panel)) { purgeDetached(); return; }

    if (e.key === 'Escape') {
      if (top.escape && !e.defaultPrevented) { e.preventDefault(); top.escape(); }
      return;
    }
    if (e.key !== 'Tab') return;

    var list = focusables(top.panel);
    if (!list.length) { e.preventDefault(); try { top.panel.focus({ preventScroll: true }); } catch (x) { /* no-op */ } return; }
    var first = list[0];
    var last = list[list.length - 1];
    var active = document.activeElement;
    var inside = top.panel.contains(active);
    if (e.shiftKey && (!inside || active === first || active === top.panel)) {
      e.preventDefault(); last.focus({ preventScroll: true });
    } else if (!e.shiftKey && (!inside || active === last)) {
      e.preventDefault(); first.focus({ preventScroll: true });
    }
  });

  /* back/forward cache: never come back to a page with something stuck inert */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    stack.slice().forEach(function (entry) { releaseBackground(entry.held); restoreReleased(entry.released); });
    stack = [];
  });

  window.mcDialog = { open: open, close: close, closed: closed };
})();
